import { FormEvent, useState } from 'react';
import { CouncilRegion } from 'shared-components';
import EvidenceFilesField from './EvidenceFilesField';
import { FoodLicenseFormErrors, FoodLicenseFormState } from './InsuranceLicenseTypes';

type Props = {
	form: FoodLicenseFormState;
	errors: FoodLicenseFormErrors;
	evidenceFiles: File[];
	evidenceError?: string;
	isSaving: boolean;
	councilRegions: CouncilRegion[];
	onSubmit: (event: FormEvent<HTMLFormElement>) => void;
	onChange: (nextForm: FoodLicenseFormState) => void;
	onAddEvidenceFiles: (files: FileList | null) => void;
	onRemoveEvidenceFile: (index: number) => void;
	showAllErrors: boolean;
	isSubmitDisabled: boolean;
	submitLabel?: string;
	submittingLabel?: string;
};

export default function FoodLicenseForm(props: Props) {
	const {
		form,
		errors,
		evidenceFiles,
		evidenceError,
		isSaving,
		councilRegions,
		onSubmit,
		onChange,
		onAddEvidenceFiles,
		onRemoveEvidenceFile,
		showAllErrors,
		isSubmitDisabled,
		submitLabel = 'Add food license',
		submittingLabel = 'Adding...',
	} = props;

	const [touched, setTouched] = useState<Partial<Record<keyof FoodLicenseFormState, boolean>>>({});

	const shouldShowError = (field: keyof FoodLicenseFormState) =>
		Boolean((showAllErrors || touched[field]) && errors[field]);

	const markTouched = (field: keyof FoodLicenseFormState) => () => setTouched((prev) => ({ ...prev, [field]: true }));

	const commonRegions = councilRegions.filter((region) => Number(region.CouncilRegionCommon) === 1);
	const uncommonRegions = councilRegions.filter((region) => Number(region.CouncilRegionCommon) !== 1);

	return (
		<form onSubmit={onSubmit}>
			<fieldset className="insurance-license-form-fieldset" disabled={isSaving}>
				<div className="insurance-license-input-row">
					<div className="insurance-license-input-item">
						<label htmlFor="FoodLicenseType">License type</label>
						<select
							id="FoodLicenseType"
							className="form-control"
							value={form.licenseType}
							onChange={(event) =>
								onChange({
									...form,
									licenseType: event.target.value as FoodLicenseFormState['licenseType'],
								})
							}
							onBlur={markTouched('licenseType')}
						>
							<option value="Temporary">Temporary</option>
							<option value="Mobile">Mobile</option>
						</select>
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="CouncilRegionId">Issuing council</label>
						<select
							id="CouncilRegionId"
							className="form-control"
							value={form.councilRegionId}
							onChange={(event) => onChange({ ...form, councilRegionId: event.target.value })}
							onBlur={markTouched('councilRegionId')}
							aria-invalid={shouldShowError('councilRegionId')}
						>
							<option value="0"></option>
							{commonRegions.length > 0 && (
								<optgroup label="Common regions">
									{commonRegions.map((region) => (
										<option key={region.CouncilRegionId} value={String(region.CouncilRegionId)}>
											{region.CouncilRegionName}
										</option>
									))}
								</optgroup>
							)}
							{uncommonRegions.length > 0 && (
								<optgroup label="Other regions">
									{uncommonRegions.map((region) => (
										<option key={region.CouncilRegionId} value={String(region.CouncilRegionId)}>
											{region.CouncilRegionName}
										</option>
									))}
								</optgroup>
							)}
						</select>
						{shouldShowError('councilRegionId') ? (
							<div className="insurance-license-field-error">{errors.councilRegionId}</div>
						) : null}
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="FoodLicenseNumber">Food license number</label>
						<input
							id="FoodLicenseNumber"
							type="text"
							className="form-control"
							value={form.licenseNumber}
							onChange={(event) => onChange({ ...form, licenseNumber: event.target.value })}
							onBlur={markTouched('licenseNumber')}
							aria-invalid={shouldShowError('licenseNumber')}
						/>
						{shouldShowError('licenseNumber') ? (
							<div className="insurance-license-field-error">{errors.licenseNumber}</div>
						) : null}
					</div>
					<div className="insurance-license-input-item">
						<label htmlFor="FoodLicenseExpiry">Expiry</label>
						<input
							id="FoodLicenseExpiry"
							type="date"
							className="form-control"
							value={form.expiryDate}
							onChange={(event) => onChange({ ...form, expiryDate: event.target.value })}
							onBlur={markTouched('expiryDate')}
							aria-invalid={shouldShowError('expiryDate')}
						/>
						{shouldShowError('expiryDate') ? (
							<div className="insurance-license-field-error">{errors.expiryDate}</div>
						) : null}
					</div>
				</div>

				<EvidenceFilesField
					inputId="FoodLicenseEvidenceFiles"
					files={evidenceFiles}
					error={evidenceError}
					showError={showAllErrors}
					disabled={isSaving}
					onAddFiles={onAddEvidenceFiles}
					onRemoveFile={onRemoveEvidenceFile}
				/>

				<div className="insurance-license-form-actions">
					<button type="submit" className="btn btn-sm btn-default" disabled={isSubmitDisabled || isSaving}>
						{isSaving ? submittingLabel : submitLabel}
					</button>
				</div>

				<div className="insurance-license-sticky-actions">
					<button type="submit" className="btn btn-primary" disabled={isSubmitDisabled || isSaving}>
						{isSaving ? submittingLabel : submitLabel}
					</button>
				</div>
			</fieldset>
		</form>
	);
}
